import { currentLocale } from "@/shared/lib/translate";
import { t as localeText } from "@/shared/lib/translate";
import { useTranslation as useLocaleSubscription } from "react-i18next";
import { useCallback, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { Spinner } from "./Spinner";
import "./ResultsScroller.css";

const positions = new Map<string, number>();
const emptyMetrics = { height: 0, range: 0, top: 0, thumb: 0 };

/** Native scrolling with a draggable, keyboard-accessible rail and a stable loading layer. */
export function ResultsScroller({ children, className, loading = false, resetKey }: {
  children: ReactNode; className?: string; loading?: boolean; resetKey?: string;
}) {
  useLocaleSubscription();
  const id = useId();
  const viewport = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const drag = useRef<{ y: number; top: number } | null>(null);
  const [metrics, setMetrics] = useState(emptyMetrics);
  const [hint, setHint] = useState<{ date: string; count: number }>();
  const [hovered, setHovered] = useState(false);
  const [dragging, setDragging] = useState(false);

  const measure = useCallback(() => {
    const node = viewport.current;
    if (!node) return;
    const height = Math.max(0, node.clientHeight - 8);
    const range = Math.max(0, node.scrollHeight - node.clientHeight);
    const thumb = Math.min(height, Math.max(36, height * node.clientHeight / Math.max(1, node.scrollHeight)));
    const next = { height, range, top: Math.min(range, Math.max(0, node.scrollTop)), thumb };
    setMetrics(previous => Object.keys(next).every(key => previous[key as keyof typeof next] === next[key as keyof typeof next]) ? previous : next);
  }, []);

  useLayoutEffect(() => {
    const node = viewport.current;
    const body = content.current;
    if (!node || !body) return;
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    observer.observe(body);
    return () => observer.disconnect();
  }, [measure]);

  useLayoutEffect(() => {
    const node = viewport.current;
    if (!node || !resetKey) return;
    node.scrollTop = positions.get(resetKey) ?? 0;
    measure();
    return () => {
      positions.set(resetKey, node.scrollTop);
      if (positions.size > 50) positions.delete(positions.keys().next().value!);
    };
  }, [resetKey, measure]);

  const updateHint = useCallback(() => {
    const node = viewport.current;
    if (!node) return;
    const top = node.getBoundingClientRect().top;
    const items = [...node.querySelectorAll<HTMLElement>("[data-scroll-date]")];
    const first = items.find(item => item.getBoundingClientRect().bottom > top + 8);
    const stamp = first?.dataset.scrollDate;
    if (!stamp || Number.isNaN(Date.parse(stamp))) { setHint(undefined); return; }
    const day = new Date(stamp).toLocaleDateString(currentLocale());
    setHint({ date: new Intl.DateTimeFormat(currentLocale(), { month: "short", day: "numeric", year: "2-digit" }).format(new Date(stamp)),
      count: items.filter(item => item.dataset.scrollDate && new Date(item.dataset.scrollDate).toLocaleDateString(currentLocale()) === day).length });
  }, []);

  const offset = metrics.range ? metrics.top / metrics.range * (metrics.height - metrics.thumb) : 0;
  return <div className="results-scroller" aria-busy={loading}>
    <div id={id} ref={viewport} data-results-scroll className="results-scroller-viewport" onScroll={() => {
      measure();
      if (hovered || dragging) updateHint();
    }}>
      <div ref={content} inert={loading || undefined} className={cn("results-scroller-content", className)}>{children}</div>
    </div>
    {metrics.range > 1 ? <div role="scrollbar" tabIndex={0} aria-label={localeText("interface.scroll_results_4b476f2d", { defaultValue: "Scroll results" })} aria-controls={id}
      aria-orientation="vertical" aria-valuemin={0} aria-valuemax={Math.round(metrics.range)} aria-valuenow={Math.round(metrics.top)}
      className="results-scrollbar" data-dragging={dragging || undefined}
      onMouseEnter={() => { setHovered(true); updateHint(); }} onMouseLeave={() => setHovered(false)}
      onFocus={() => { setHovered(true); updateHint(); }} onBlur={() => setHovered(false)}
      onKeyDown={event => {
        const node = viewport.current;
        if (!node) return;
        const targets: Record<string, number> = { ArrowUp: node.scrollTop - 48, ArrowDown: node.scrollTop + 48,
          PageUp: node.scrollTop - node.clientHeight * .9, PageDown: node.scrollTop + node.clientHeight * .9, Home: 0, End: metrics.range };
        if (!(event.key in targets)) return;
        event.preventDefault();
        node.scrollTop = targets[event.key];
        measure(); updateHint();
      }}
      onPointerDown={event => {
        if (event.button !== 0 || !viewport.current) return;
        event.preventDefault();
        event.currentTarget.focus();
        event.currentTarget.setPointerCapture(event.pointerId);
        const y = event.clientY - event.currentTarget.getBoundingClientRect().top;
        if (y < offset || y > offset + metrics.thumb) {
          viewport.current.scrollTop = Math.max(0, Math.min(1, (y - metrics.thumb / 2) / Math.max(1, metrics.height - metrics.thumb))) * metrics.range;
        }
        drag.current = { y: event.clientY, top: viewport.current.scrollTop };
        setDragging(true); measure(); updateHint();
      }}
      onPointerMove={event => {
        if (!drag.current || !viewport.current) return;
        viewport.current.scrollTop = drag.current.top + (event.clientY - drag.current.y) * metrics.range / Math.max(1, metrics.height - metrics.thumb);
        measure(); updateHint();
      }}
      onPointerUp={event => { drag.current = null; setDragging(false); event.currentTarget.releasePointerCapture(event.pointerId); }}
      onLostPointerCapture={() => { drag.current = null; setDragging(false); }}>
      <span className="results-scrollbar-thumb" style={{ height: metrics.thumb, transform: `translateY(${offset}px)` }} />
      {hint && (hovered || dragging) ? <span className="results-scrollbar-hint" aria-hidden="true"
        style={{ top: Math.max(0, Math.min(metrics.height - 64, offset + metrics.thumb / 2 - 28)) }}>
        <strong>{hint.date}</strong><span>{localeText("interface.sentence__value0_value1_loaded_35e2e688", { defaultValue: "{{value0}} {{value1}} loaded", value0: hint.count, value1: hint.count === 1 ? localeText("interface.asset_05fac943", { defaultValue: "asset" }) : localeText("interface.assets_3685e330", { defaultValue: "assets" }) })}</span>
      </span> : null}
    </div> : null}
    {loading ? <div className="results-loading-overlay" role="status"><span><Spinner size={20} />{localeText("interface.updating_results_1d61d732", { defaultValue: "Updating results…" })}</span></div> : null}
  </div>;
}
